import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEyeSlash } from '@fortawesome/pro-regular-svg-icons';
import { faHeart as faHeartSolid } from '@fortawesome/pro-solid-svg-icons';
import PropTypes from 'prop-types';
import cn from 'classnames';
import _ from 'lodash';
import './ShopPageHeader.scss';

import { openCuratorGroupModal, focusShopSearch, openNewCircleModal } from '../../../Actions/UIActions';
import { updateCurrentUser } from '../../../Actions/UserActions';
import { createCuratorGroupFavorite, deleteCuratorGroupFavorite, updateCuratorGroup } from '../../../Actions/ShopActions';
import {
  getCuratorGroupsIncludingUserId,
  getCuratorGroupFavorites,
  isCuratorGroupIdMine,
  getUserId,
  getCuratorGroups,
  getName,
  getBio,
  getSocialLinks
} from '../../../Helpers/user_helpers';
import { isAnalyticsMode } from '../../../Helpers/ui_helpers';
import { getLinkToPublicCuratorGroupPage, getLinkToPrivateCuratorGroupPage } from '../../../Helpers/shop_helpers';
import { matchScrollHeight } from '../../../Helpers/helpers';
import { getSocialIconsForArtist } from '../../../Helpers/social_helpers';
import { tooltipKeys } from '../../../Helpers/onboarding_helpers';
import { getUserTierDisplay } from '../../../Helpers/tier_helpers';
import { getPrettyNumber } from '../../../Helpers/formatting';

import Tooltip from '../../General/Tooltip';
import TrainingTooltip from '../../General/TrainingTooltip';

import ShopActionsPanel from './ShopActionsPanel';
import ShopPageHeaderImage from './ShopPageHeaderImage';
import ShopPageHeaderEditSocialLinks from './ShopPageHeaderEditSocialLinks';

const ShopPageHeader = props => {
  const { user, activeRefinementObjects, isYourShop, query } = props;
  const { activeCurator, activeCuratorGroup, activeCategory, activeDepartment, activeIndustry, activeBrands } = activeRefinementObjects;
  const curatorGroupsForActiveCurator = activeCurator ? getCuratorGroupsIncludingUserId(user, activeCurator.id) : [];
  const curatorGroupFavorite = activeCuratorGroup ? getCuratorGroupFavorites(user)?.find(f => f.CuratorGroup_id === activeCuratorGroup.id) : null;
  const hasSearchQuery = query && query.length > 0;

  // Header Data
  let name, bio, social_links;
  if (isYourShop) {
    name = getName(user);
    bio = getBio(user);
    social_links = getSocialLinks(user);
  } else if (activeCurator) {
    name = activeCurator.name;
    bio = activeCurator.description;
    social_links = activeCurator.social_links;
  } else if (activeCuratorGroup) {
    name = activeCuratorGroup.title;
  } else if (props.Curator_id || props.Curator_username || props.CuratorGroup_id) {
    name = '';
  } else {
    name = 'All ShopMy curators';
  }

  /*
    Favoriting Logic
  */
  const isMyGroup = activeCuratorGroup && isCuratorGroupIdMine(user, activeCuratorGroup.id);
  const [isFavoriting, setIsFavoriting] = React.useState(false);
  const hasCuratorGroups = getCuratorGroups(user).length > 0;
  const isFavorited = curatorGroupsForActiveCurator.length > 0 || !!curatorGroupFavorite;

  const pressHeart = e => {
    e.stopPropagation();
    if (isMyGroup) return;

    window.__ADD_EVENT__('Shop - Clicked Heart', {
      Curator: activeCurator?.name || 'None',
      'Curator Group': activeCuratorGroup?.title || 'None',
      'Has Curator Groups': hasCuratorGroups
    });

    if (activeCurator) {
      if (hasCuratorGroups) {
        // If they have circles, we want to open the add curator modal for quick addition
        props.openCuratorGroupModal({
          source: 'Shop Header',
          params: {
            type: 'add',
            curator: activeCurator,
            curatorGroup: activeCuratorGroup
          }
        });
      } else {
        // If they don't have any circles, we want to open the create circle modal
        props.openNewCircleModal({
          params: {
            panel: 'final',
            initial_curators: [activeCurator]
          }
        });
      }
    } else if (activeCuratorGroup) {
      // If they have circles, we want to allow them to favorite or unfavorite the circle
      if (isFavorited) {
        props.deleteCuratorGroupFavorite(curatorGroupFavorite, { source: 'shop_header' }).then(() => {
          window.ALERT.success(`Unfavorited the shopping circle "${activeCuratorGroup.title}"`);
        });
      } else {
        if (isFavoriting) return;
        setIsFavoriting(true);
        props
          .createCuratorGroupFavorite(activeCuratorGroup, { source: 'shop_header' })
          .then(resp => {
            resp.groupFavorite && window.ALERT.success(`Favorited the shopping circle "${activeCuratorGroup.title}"`);
          })
          .finally(() => {
            setIsFavoriting(false);
          });
      }
    }
  };

  /*
    Circle Management
  */
  const clickToAddCuratorsToCircle = () => {
    props.openNewCircleModal({
      params: {
        panel: 'intro',
        initial_group: activeCuratorGroup
      }
    });
  };

  // UI for category
  let curatedByParts = [];

  // Add the category, department, or industry
  if (activeCategory) curatedByParts.push({ display: activeCategory.name, unrefine: () => props.clearCategoryId(activeCategory.id) });
  else if (activeDepartment)
    curatedByParts.push({
      display: activeDepartment.name,
      unrefine: () => props.clearDepartmentId(activeDepartment.id)
    });
  else if (activeIndustry) curatedByParts.push({ display: activeIndustry.name, unrefine: () => props.clearIndustryId(activeIndustry.id) });
  else if (hasSearchQuery) curatedByParts.push({ display: query, unrefine: () => props.setQuery('') });

  // Add the brand
  if (activeBrands) {
    if (curatedByParts.length) curatedByParts.push({ display: ', by ' });
    curatedByParts.push({
      display: activeBrands.map(b => b.name).join(', '),
      unrefine: () => props.clearBrandIds(activeBrands.map(b => b.id))
    });
  }

  // Add the curated text
  curatedByParts.push({ display: curatedByParts.length ? ', curated by' : 'Curated by', unrefine: null });

  // UI Management
  const [showFullBio, setShowFullBio] = React.useState(false);
  const showAddToCircleTooltip = !hasCuratorGroups && !!activeCurator;
  const numViews = props.currentAnalytics?.shop_stats?.num_views;

  // Actions Management
  let actions = [];
  if (props.isCuratorGroupShop) {
    if (isMyGroup) {
      // Allow them to add new curators to the group
      actions.push({
        label: 'Add Curators',
        onClick: clickToAddCuratorsToCircle
      });
    } else if (isFavorited) {
      // Allow them to unfavorite the group
      // actions.push({ label: 'Unfavorite', onClick: pressHeart }); // Save in case we want to use this again
    } else {
      // Allow them to favorite the group
      actions.push({
        label: isFavoriting ? 'Favoriting...' : 'Favorite',
        onClick: pressHeart
      });
    }
  } else if (props.isCuratorShop) {
    if (!isFavorited && !isYourShop) {
      actions.push({
        label: 'Add to circle',
        onClick: pressHeart,
        showAddToCircleTooltip: showAddToCircleTooltip
      });
    }
  }

  // Editing
  const canShowBio = bio || (props.isFetchingActiveRefinementObjects && props.isCuratorShop);
  const [updatedName, setUpdatedName] = React.useState('');
  const [updatedBio, setUpdatedBio] = React.useState('');
  const [updatedSocialLinks, setUpdatedSocialLinks] = React.useState('');

  // UI Management
  const canShowStatus =
    (activeCurator && !!activeCurator.trustedByCount) || (props.isFetchingActiveRefinementObjects && props.isCuratorShop) || isYourShop;
  const [isEditingShopData, setIsEditingShopData] = React.useState(false);
  const [isSavingShopData, setIsSavingShopData] = React.useState(false);
  const startEditingShopData = () => {
    setUpdatedName(name);
    setUpdatedBio(bio);
    setUpdatedSocialLinks(social_links || '');
    setIsEditingShopData(true);
  };
  const doneEditingShopData = (shouldSave = true) => {
    const hasChangedName = getName(user) !== updatedName;
    const hasChangedBio = getBio(user) !== updatedBio;
    const hasChangedSocialLinks = social_links !== updatedSocialLinks;
    const needsUpdate = hasChangedName || hasChangedBio || hasChangedSocialLinks;
    if (!needsUpdate) return setIsEditingShopData(false);

    if (shouldSave) {
      setIsSavingShopData(true);
      props
        .updateCurrentUser({ name: updatedName, description: updatedBio, social_links: updatedSocialLinks }, getUserId(props.user))
        .catch(() => window.ALERT.error('Error updating shop data, please try again.'))
        .finally(() => {
          setIsSavingShopData(false);
          setIsEditingShopData(false);
          window.ALERT.success('Shop data updated.');
        });
    } else {
      setIsEditingShopData(false);
    }
  };

  // Class Management
  const isLoading = props.isFetchingActiveRefinementObjects && !activeCurator && !activeCuratorGroup;
  const additionalClasses = {
    loading: isLoading
  };

  // UI Management
  const showTierDisplay = activeCurator && activeCurator.tier <= 2;
  const showTrustCount = (activeCurator && !activeCurator.hideTrustCountFromShop) || (activeCurator && isYourShop);

  return (
    <header className={cn('shop-page-header-container', additionalClasses)}>
      <ShopActionsPanel
        isFetchingAnalytics={props.isFetchingAnalytics}
        isEditingShopData={isEditingShopData}
        isSavingShopData={isSavingShopData}
        startEditingShopData={startEditingShopData}
        doneEditingShopData={doneEditingShopData}
        activeRefinementObjects={activeRefinementObjects}
        isYourShop={isYourShop}
        ui={props.ui}
      />
      <div className={cn('main-panel', additionalClasses)}>
        <div className='header-data-section'>
          <div className='image-container'>
            <ShopPageHeaderImage activeRefinementObjects={props.activeRefinementObjects} isLoading={isLoading} />
          </div>
          <div className='curated-by-container'>
            {isAnalyticsMode(props.ui) && (
              <div className='views-count'>{props.isFetchingAnalytics ? '...' : `${numViews} shop view${numViews !== 1 ? 's' : ''}`}</div>
            )}
            <div className={cn('label-container', additionalClasses)}>
              <h2 className={cn('label', additionalClasses)}>
                {curatedByParts.map((part, idx) => {
                  return (
                    <span key={idx} className={cn('curated-by-part', { clickable: !!part.unrefine })} onClick={part.unrefine}>
                      {part.display}
                    </span>
                  );
                })}
              </h2>
            </div>
            <div className={cn('name-container', additionalClasses)}>
              {isEditingShopData ? (
                <textarea placeholder='Your Full Name' rows={1} className='name' value={updatedName} onChange={e => setUpdatedName(e.target.value)} />
              ) : (
                <h1 className={cn('name', additionalClasses)}>{isLoading ? '...' : name}</h1>
              )}
            </div>
          </div>
          {canShowBio && (
            <div className={cn('bio-container', additionalClasses)}>
              {isEditingShopData ? (
                <textarea
                  ref={matchScrollHeight}
                  rows={3}
                  className='bio'
                  placeholder='Describe a little bit about who you are.'
                  value={updatedBio}
                  onChange={e => setUpdatedBio(e.target.value)}
                />
              ) : (
                <h3 onClick={() => setShowFullBio(!showFullBio)} className={cn('bio', { 'show-full-bio': showFullBio })}>
                  {bio}
                </h3>
              )}
            </div>
          )}
          {activeCuratorGroup && (
            <Link
              to={isMyGroup ? getLinkToPrivateCuratorGroupPage(activeCuratorGroup) : getLinkToPublicCuratorGroupPage(activeCuratorGroup)}
              className='view-curators-container'
            >
              <div className='view-curators'>
                View {activeCuratorGroup.num_curators} curator{activeCuratorGroup.num_curators !== 1 ? 's' : ''}
              </div>
            </Link>
          )}
          {!!actions.length && (
            <div className={cn('actions-container', additionalClasses)}>
              {actions.map(action => {
                const wrapInTooltip = el => {
                  return action.showAddToCircleTooltip ? (
                    <TrainingTooltip
                      visible
                      key={action.label}
                      trainingKey={tooltipKeys.ADD_TO_CIRCLE_FROM_SHOP_PAGE_HEADER}
                      fadeInDelay={2_000}
                      fadeOutDelay={10_000}
                      message={
                        <>
                          Favorite this curator by adding them to your <b>first circle</b>.
                        </>
                      }
                      numDismissalsToHide={window.__IS_PROD__ ? 3 : 1_000}
                      position='right'
                      positionIfInitialOffScreen='top'
                    >
                      {el}
                    </TrainingTooltip>
                  ) : (
                    el
                  );
                };
                return wrapInTooltip(
                  <div key={action.label} className={cn('action', additionalClasses, action.additionalClasses || '')} onClick={action.onClick}>
                    {action.label}
                  </div>
                );
              })}
            </div>
          )}
          {canShowStatus && (
            <div className={cn('status-container', additionalClasses)}>
              <div className={cn('status', { hidden: activeCurator?.hideTrustCountFromShop })}>
                {showTierDisplay && <div>{getUserTierDisplay(activeCurator.tier)}</div>}
                {showTierDisplay && showTrustCount && <div className='bullet'> • </div>}
                {showTrustCount && (
                  <div>
                    Trusted by {getPrettyNumber(activeCurator.trustedByCount)} {activeCurator.isShopper ? 'other ' : ''}shopper
                    {activeCurator.trustedByCount !== 1 ? 's' : ''}
                  </div>
                )}
                {!!activeCurator?.hideTrustCountFromShop && (
                  <Tooltip message='You have hidden the "Trusted By" count from your shop, you can edit this in account settings.'>
                    <FontAwesomeIcon className='hide-trust-count-icon' icon={faEyeSlash} />
                  </Tooltip>
                )}
              </div>
            </div>
          )}
          {(props.isCuratorShop || (props.isCuratorGroupShop && isFavorited)) && (
            <div className={cn('social-icons-container', additionalClasses)}>
              {social_links ? (
                isEditingShopData ? (
                  <ShopPageHeaderEditSocialLinks social_links={updatedSocialLinks || social_links} setUpdatedSocialLinks={setUpdatedSocialLinks} />
                ) : (
                  getSocialIconsForArtist({ ...activeCurator, social_links }, { iconStyle: 'dark' })
                )
              ) : null}
              {isFavorited && (
                <Tooltip message={props.isCuratorShop ? 'In Your Circle' : 'Click to Unfavorite'}>
                  <div onClick={pressHeart} className='in-circle-container'>
                    <div className='in-circle'>
                      <FontAwesomeIcon icon={faHeartSolid} />
                    </div>
                  </div>
                </Tooltip>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

ShopPageHeader.propTypes = {
  // From Inside
  user: PropTypes.object.isRequired,
  shop: PropTypes.object.isRequired,
  ui: PropTypes.object.isRequired,
  openCuratorGroupModal: PropTypes.func.isRequired,
  createCuratorGroupFavorite: PropTypes.func.isRequired,
  deleteCuratorGroupFavorite: PropTypes.func.isRequired,
  updateCuratorGroup: PropTypes.func.isRequired,
  updateCurrentUser: PropTypes.func.isRequired,
  focusShopSearch: PropTypes.func.isRequired,
  openNewCircleModal: PropTypes.func.isRequired,

  // From Outside
  isFetchingProductRefinements: PropTypes.bool.isRequired,
  isFetchingActiveRefinementObjects: PropTypes.bool.isRequired,
  activeRefinementObjects: PropTypes.object.isRequired,
  clearCategoryId: PropTypes.func.isRequired,
  clearDepartmentId: PropTypes.func.isRequired,
  clearIndustryId: PropTypes.func.isRequired,
  isCuratorShop: PropTypes.bool.isRequired,
  isYourShop: PropTypes.bool.isRequired,
  currentAnalytics: PropTypes.object,
  isFetchingAnalytics: PropTypes.bool.isRequired
};

const mapStateToProps = state => {
  const { user, shop, ui } = state;
  return { user, shop, ui };
};

export default connect(mapStateToProps, {
  openCuratorGroupModal,
  createCuratorGroupFavorite,
  deleteCuratorGroupFavorite,
  updateCuratorGroup,
  updateCurrentUser,
  focusShopSearch,
  openNewCircleModal
})(ShopPageHeader);
